/**
 * Test emails sent from the Cognito email templates (MET-3729).
 *
 * Wellspring invokes the `cognito-email-test-sender` Lambda in common-infra,
 * which renders a template with placeholder credentials (never a real Cognito
 * code) and sends it with a `[TEST]` subject to an allowed internal domain.
 */

/** Cognito email templates that can be sent as a test */
export const TEST_EMAIL_TEMPLATES = [
  'admin-invite',
  'signup',
  'forgot-password',
  'verify-attribute',
  'auth-challenge',
  'generic',
] as const;

export type TestEmailTemplate = (typeof TEST_EMAIL_TEMPLATES)[number];

/** Human-readable labels for the templates */
export const TEST_EMAIL_TEMPLATE_LABELS: Record<TestEmailTemplate, string> = {
  'admin-invite': 'Onboarding invite (temporary password)',
  'signup': 'Sign-up verification code',
  'forgot-password': 'Password reset',
  'verify-attribute': 'Verify new email',
  'auth-challenge': 'Sign-in code',
  'generic': 'Generic notification',
};

export const TEST_EMAIL_ENVIRONMENTS = ['production', 'development'] as const;
export type TestEmailEnvironment = (typeof TEST_EMAIL_ENVIRONMENTS)[number];

export const TEST_EMAIL_PRODUCTS = ['plus', 'my-waterscope'] as const;
export type TestEmailProduct = (typeof TEST_EMAIL_PRODUCTS)[number];

export interface SendTestEmailRequest {
  template: TestEmailTemplate;
  /** Recipient; defaults to the caller's own email */
  to?: string;
  environment?: TestEmailEnvironment;
  product?: TestEmailProduct;
  /** Render and return the email without sending it */
  dryRun?: boolean;
}

export interface SendTestEmailResponse {
  template: TestEmailTemplate;
  to: string;
  subject: string;
  sent: boolean;
  htmlContent: string;
  textContent: string;
}
